import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { 
  Brain, 
  Heart, 
  Compass, 
  ArrowRight, 
  CheckCircle2, 
  AlertTriangle, 
  Clock, 
  Sparkles,
  ChevronDown,
  Mail
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_alma-metrics/artifacts/usza7456_Alquimia.png";

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.15 } }
};

export default function HomePage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-cream to-cream-alt relative overflow-hidden">
      {/* Decorative blobs */}
      <div className="absolute top-20 right-0 w-96 h-96 blob-coral rounded-full opacity-50" />
      <div className="absolute top-96 left-0 w-80 h-80 blob-lavender rounded-full opacity-40" />
      <div className="absolute bottom-40 right-20 w-64 h-64 blob-coral rounded-full opacity-30" />
      
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass-card border-b border-plum/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Alquimia del Alma" className="h-10 w-10 rounded-full shadow-soft" />
              <span className="font-heading text-lg text-plum hidden sm:block">Alquimia del Alma</span>
            </div>
            <Button 
              onClick={() => navigate("/evaluacion")}
              className="btn-coral px-6 py-2 text-sm"
              data-testid="nav-start-btn"
            >
              Iniciar Evaluación
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center pt-16">
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="space-y-8"
          >
            <motion.div variants={fadeInUp} className="flex justify-center mb-6">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-coral/20 to-lavender/20 rounded-full blur-2xl scale-150 animate-pulse-glow" />
                <img 
                  src={LOGO_URL} 
                  alt="Alquimia del Alma" 
                  className="relative h-28 w-28 sm:h-36 sm:w-36 animate-float rounded-full shadow-glow"
                />
              </div>
            </motion.div>
            
            <motion.h1 
              variants={fadeInUp}
              className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-plum leading-tight"
            >
              ¿Vives en <span className="gradient-text">Coherencia</span>
              <br />Contigo Mismo?
            </motion.h1>
            
            <motion.p 
              variants={fadeInUp}
              className="text-lg sm:text-xl text-plum-light max-w-2xl mx-auto font-light"
            >
              Descubre la alineación entre tu pensar, sentir y actuar.
              <br className="hidden sm:block" />
              Basado en neurociencia + ontología del lenguaje.
            </motion.p>
            
            <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
              <Button 
                onClick={() => navigate("/evaluacion")}
                className="btn-coral px-10 py-6 text-lg flex items-center gap-2"
                data-testid="hero-start-btn"
              >
                Evaluar Mi Coherencia
                <ArrowRight className="w-5 h-5" />
              </Button>
            </motion.div>
            
            <motion.div 
              variants={fadeInUp}
              className="flex items-center justify-center gap-6 text-sm text-plum-muted pt-4"
            >
              <span className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-coral" /> 10-12 minutos
              </span>
              <span className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-lavender" /> 100% Gratuito
              </span>
            </motion.div>
          </motion.div>
          
          {/* Scroll indicator */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.5 }}
            className="absolute bottom-8 left-1/2 -translate-x-1/2"
          >
            <ChevronDown className="w-8 h-8 text-coral/50 animate-bounce" />
          </motion.div>
        </div>
      </section>

      {/* Why It Matters Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 relative section-accent">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.h2 
              variants={fadeInUp}
              className="font-heading text-3xl sm:text-4xl font-bold text-plum mb-4"
            >
              ¿Por Qué es Importante la Coherencia?
            </motion.h2>
            <motion.p 
              variants={fadeInUp}
              className="text-plum-light max-w-2xl mx-auto"
            >
              La disonancia entre lo que piensas, sientes y haces tiene un costo invisible pero profundo
            </motion.p>
          </motion.div>
          
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid md:grid-cols-3 gap-6"
          >
            {[
              {
                icon: AlertTriangle,
                title: "Estrés Crónico",
                description: "Tu sistema nervioso gasta energía constante tratando de mantener una imagen que no corresponde con tu interior.",
                color: "text-coral"
              },
              {
                icon: Heart,
                title: "Relaciones Insatisfactorias",
                description: "Cuando no te muestras auténtico, las conexiones permanecen superficiales y carentes de intimidad real.",
                color: "text-lavender"
              },
              {
                icon: Compass,
                title: "Sensación de Vacío",
                description: "Vivir desalineado genera una desconexión profunda contigo mismo y con tu propósito de vida.",
                color: "text-amber-warm"
              }
            ].map((item, i) => (
              <motion.div
                key={i}
                variants={fadeInUp}
                className="glass-card-solid rounded-3xl p-8 card-hover"
              >
                <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br from-coral/10 to-lavender/10 flex items-center justify-center mb-5`}>
                  <item.icon className={`w-7 h-7 ${item.color}`} />
                </div>
                <h3 className="font-heading text-xl font-semibold text-plum mb-3">{item.title}</h3>
                <p className="text-plum-muted text-sm leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-white/50">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.h2 
              variants={fadeInUp}
              className="font-heading text-3xl sm:text-4xl font-bold text-plum mb-4"
            >
              Cómo Funciona
            </motion.h2>
          </motion.div>
          
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid md:grid-cols-3 gap-8"
          >
            {[
              {
                step: "01",
                title: "Evaluación Multidimensional",
                description: "22 preguntas diseñadas para explorar tus valores, emociones, conductas y narrativa personal.",
                icon: Brain
              },
              {
                step: "02",
                title: "Análisis Profundo",
                description: "Algoritmo basado en neurociencia y ontología que identifica áreas de disonancia específicas.",
                icon: Sparkles
              },
              {
                step: "03",
                title: "Plan de Acción",
                description: "Recibe un reporte detallado con prácticas personalizadas para recuperar tu coherencia.",
                icon: CheckCircle2
              }
            ].map((item, i) => (
              <motion.div
                key={i}
                variants={fadeInUp}
                className="relative"
              >
                <div className="text-7xl font-heading font-bold gradient-text-warm opacity-20 absolute -top-8 left-0">
                  {item.step}
                </div>
                <div className="pt-10 pl-4">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-coral to-coral-dark flex items-center justify-center mb-5 shadow-glow">
                    <item.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-plum mb-3">{item.title}</h3>
                  <p className="text-plum-muted text-sm leading-relaxed">{item.description}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
          
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center mt-16"
          >
            <Button 
              onClick={() => navigate("/evaluacion")}
              className="btn-coral px-12 py-6 text-lg"
              data-testid="how-it-works-start-btn"
            >
              Comenzar Ahora
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Quote Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-coral/5 via-transparent to-lavender/5" />
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="max-w-4xl mx-auto text-center relative z-10"
        >
          <div className="glass-card-solid rounded-3xl p-12">
            <blockquote className="font-heading text-2xl sm:text-3xl lg:text-4xl text-plum italic leading-relaxed">
              "El verdadero viaje de descubrimiento no consiste en buscar nuevos paisajes, 
              <span className="gradient-text"> sino en tener nuevos ojos</span>"
            </blockquote>
            <p className="mt-6 text-plum-muted">— Marcel Proust</p>
          </div>
        </motion.div>
      </section>

      {/* About Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 section-accent">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="glass-card-solid rounded-3xl p-8 md:p-12"
          >
            <motion.h2 
              variants={fadeInUp}
              className="font-heading text-2xl sm:text-3xl font-bold text-plum mb-6"
            >
              Sobre la Coherencia Emocional
            </motion.h2>
            <motion.p 
              variants={fadeInUp}
              className="text-plum-light leading-relaxed mb-6"
            >
              La coherencia emocional es el estado de alineación entre tus valores, emociones, 
              pensamientos y acciones. Cuando vives en coherencia, experimentas mayor paz interior, 
              relaciones más auténticas, y capacidad de actuar desde tu propósito.
            </motion.p>
            <motion.p 
              variants={fadeInUp}
              className="text-plum-light leading-relaxed mb-8"
            >
              La disonancia, por el contrario, genera estrés crónico, insatisfacción y pérdida de vitalidad. 
              Esta herramienta te ayuda a identificar exactamente dónde están tus áreas de incoherencia 
              y te ofrece un camino práctico para recuperar tu integridad.
            </motion.p>
            <motion.div 
              variants={fadeInUp}
              className="flex items-center gap-4 pt-4 border-t border-plum/10"
            >
              <img 
                src={LOGO_URL} 
                alt="Alquimia del Alma" 
                className="w-14 h-14 rounded-full shadow-soft"
              />
              <div>
                <p className="text-plum font-medium">Julián López</p>
                <p className="text-plum-muted text-sm">Coach Ontológico, Master en Psicología Espiritual</p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 relative">
        <div className="absolute inset-0 bg-gradient-to-t from-coral/5 to-transparent" />
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl mx-auto text-center relative z-10"
        >
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-plum mb-6">
            ¿Listo para Descubrir tu Nivel de Coherencia?
          </h2>
          <p className="text-plum-light mb-8 max-w-xl mx-auto">
            El primer paso hacia la transformación es la consciencia. 
            Dedica 12 minutos a conocerte más profundamente.
          </p>
          <Button 
            onClick={() => navigate("/evaluacion")}
            className="btn-coral px-12 py-6 text-lg flex items-center gap-2 mx-auto"
            data-testid="cta-start-btn"
          >
            Comenzar Evaluación Gratuita
            <ArrowRight className="w-5 h-5" />
          </Button>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 border-t border-plum/10 bg-white/30">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Alquimia del Alma" className="h-10 w-10 rounded-full" />
              <span className="font-heading text-lg text-plum">Alquimia del Alma</span>
            </div>
            <div className="flex items-center gap-4 text-plum-muted text-sm">
              <a href="https://www.alquimiadelalma.click" target="_blank" rel="noopener noreferrer" className="hover:text-coral transition-colors">
                Web Principal
              </a>
              <span>•</span>
              <a href="mailto:agenda@alquimiadelalma.click" className="hover:text-coral transition-colors flex items-center gap-1">
                <Mail className="w-4 h-4" /> Contacto
              </a>
            </div>
            {/* Insignia oficial de Google Play: solo se escala, no se recorta ni recolorea. */}
            <a
              href="https://play.google.com/store/apps/details?id=com.alquimiadelalma.app"
              target="_blank"
              rel="noopener noreferrer"
              data-umami-event="app-play-coherencia"
              aria-label="Descargar la app Alquimia del Alma en Google Play"
              className="inline-block leading-none"
            >
              <img
                src="/google-play-badge.png"
                alt="Disponible en Google Play"
                width="150"
                height="58"
                loading="lazy"
                className="h-[58px] w-auto block"
              />
            </a>
          </div>
          <div className="mt-8 pt-8 border-t border-plum/5 text-center">
            <p className="text-plum-muted text-xs max-w-2xl mx-auto">
              Esta herramienta es educativa y de desarrollo personal. No reemplaza terapia psicológica 
              o atención médica profesional. Si experimentas dificultades significativas, consulta a un 
              profesional de salud mental.
            </p>
            <p className="text-plum-muted/50 text-xs mt-4">
              © {new Date().getFullYear()} Alquimia del Alma. Todos los derechos reservados.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
